import React from "react";
import { Col, Input, Label, Row } from "reactstrap";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getClientOptions } from "features/Clients/services/ClientService";
import { getAmcOptions } from "features/Amc/services/AmcService";
import {
  AmcVisitStatus,
  amcVisitStatusMap,
} from "features/AmcVisitLogs/constants/amc-visit.constants";
import {
  AmcContractStatus,
  amcStatusMap,
} from "features/Amc/constants/amc-contract.constants";
import type { AmcVisitLogListParams } from "features/AmcVisitLogs/services/AmcVisitService";
import type { SelectOption } from "types/api.types";

export type VisitLogFiltersState = {
  visitStatuses: AmcVisitStatus[];
  amcStatuses: AmcContractStatus[];
  clientId: number | "";
  visitNumber: number | "";
  address: string;
  city: string;
  state: string;
  /** AMC contract id from `/amcs/options`. */
  amcId: number | "";
};

export const emptyVisitLogFilters = (): VisitLogFiltersState => ({
  visitStatuses: [],
  amcStatuses: [],
  clientId: "",
  visitNumber: "",
  address: "",
  city: "",
  state: "",
  amcId: "",
});

const VISIT_STATUS_ORDER = [
  AmcVisitStatus.PLANNED,
  AmcVisitStatus.IN_PROGRESS,
  AmcVisitStatus.COMPLETED,
  AmcVisitStatus.MISSED,
  AmcVisitStatus.CANCELLED,
] as const;

const AMC_STATUS_ORDER = [
  AmcContractStatus.DRAFT,
  AmcContractStatus.READY_FOR_ACTIVATION,
  AmcContractStatus.ACTIVE,
  AmcContractStatus.COMPLETED,
  AmcContractStatus.CANCELLED,
] as const;

function toggleEnum<T extends string>(list: T[], value: T): T[] {
  return list.includes(value)
    ? list.filter((x) => x !== value)
    : [...list, value];
}

export type VisitLogListFiltersProps = {
  filters: VisitLogFiltersState;
  onChange: (next: VisitLogFiltersState) => void;
};

/** Maps UI state to GET /amc-visits query fields (excluding page, limit, search). */
export function visitLogFiltersToListParams(
  f: VisitLogFiltersState,
): Partial<AmcVisitLogListParams> {
  const trim = (s: string) => s.trim();
  return {
    ...(f.visitStatuses.length ? { status: f.visitStatuses.join(",") } : {}),
    ...(f.amcStatuses.length ? { amcStatus: f.amcStatuses.join(",") } : {}),
    ...(f.clientId !== "" &&
    Number.isFinite(f.clientId) &&
    Number(f.clientId) > 0
      ? { clientId: Number(f.clientId) }
      : {}),
    ...(f.visitNumber !== "" &&
    Number.isFinite(f.visitNumber) &&
    Number(f.visitNumber) > 0
      ? { visitNumber: Number(f.visitNumber) }
      : {}),
    ...(trim(f.address) ? { address: trim(f.address) } : {}),
    ...(trim(f.city) ? { city: trim(f.city) } : {}),
    ...(trim(f.state) ? { state: trim(f.state) } : {}),
    ...(f.amcId !== "" &&
    Number.isFinite(f.amcId) &&
    Number(f.amcId) > 0
      ? { amcId: Number(f.amcId) }
      : {}),
  };
}

export function countVisitLogFiltersActive(f: VisitLogFiltersState): number {
  let n = 0;
  if (f.visitStatuses.length) n += 1;
  if (f.amcStatuses.length) n += 1;
  if (f.clientId !== "") n += 1;
  if (f.visitNumber !== "") n += 1;
  if (f.address.trim()) n += 1;
  if (f.city.trim()) n += 1;
  if (f.state.trim()) n += 1;
  if (f.amcId !== "") n += 1;
  return n;
}

export default function VisitLogListFilters({
  filters,
  onChange,
}: VisitLogListFiltersProps) {
  const patch = (partial: Partial<VisitLogFiltersState>) =>
    onChange({ ...filters, ...partial });

  return (
    <div className="w-100">
      <Row className="g-2 g-md-3 mb-2">
        <Col xs={12}>
          <Label className="form-label text-muted small mb-1">
            Visit status
          </Label>
          <div className="d-flex flex-wrap gap-2">
            {VISIT_STATUS_ORDER.map((s) => (
              <label
                key={s}
                className="form-check form-check-inline mb-0 small user-select-none"
              >
                <Input
                  type="checkbox"
                  className="form-check-input"
                  checked={filters.visitStatuses.includes(s)}
                  onChange={() =>
                    patch({
                      visitStatuses: toggleEnum(filters.visitStatuses, s),
                    })
                  }
                />
                <span className="form-check-label ms-1">
                  {amcVisitStatusMap[s]?.label ?? s}
                </span>
              </label>
            ))}
          </div>
        </Col>
      </Row>

      <Row className="g-2 g-md-3 mb-2">
        <Col xs={12}>
          <Label className="form-label text-muted small mb-1">
            Contract (AMC) status
          </Label>
          <div className="d-flex flex-wrap gap-2">
            {AMC_STATUS_ORDER.map((s) => (
              <label
                key={s}
                className="form-check form-check-inline mb-0 small user-select-none"
              >
                <Input
                  type="checkbox"
                  className="form-check-input"
                  checked={filters.amcStatuses.includes(s)}
                  onChange={() =>
                    patch({
                      amcStatuses: toggleEnum(filters.amcStatuses, s),
                    })
                  }
                />
                <span className="form-check-label ms-1">
                  {amcStatusMap[s]?.label ?? s}
                </span>
              </label>
            ))}
          </div>
        </Col>
      </Row>

      <Row className="g-2 g-md-3 align-items-end mb-2">
        <Col xs={12} sm={6} md={6}>
          <Label className="form-label text-muted small mb-1">Client</Label>
          <AsyncPaginatedSelect
            fetchOptions={getClientOptions}
            placeholder="Any client…"
            value={filters.clientId === "" ? "" : filters.clientId}
            onChange={(opt: SelectOption | SelectOption[] | null) => {
              const val = Array.isArray(opt) ? (opt[0] ?? null) : opt;
              const v = val?.value;
              patch({
                clientId:
                  typeof v === "number" && Number.isFinite(v) && v > 0 ? v : "",
              });
            }}
          />
        </Col>
        <Col xs={12} sm={6} md={6}>
          <Label className="form-label text-muted small mb-1">
            Visit number
          </Label>
          <Input
            type="number"
            min={1}
            placeholder="e.g. 1"
            value={filters.visitNumber === "" ? "" : filters.visitNumber}
            onChange={(e) => {
              const v = e.target.value;
              patch({
                visitNumber: v === "" ? "" : Number(v),
              });
            }}
          />
        </Col>
      </Row>

      <Row className="g-2 g-md-3 align-items-end mb-2">
        <Col xs={12} md={4}>
          <Label className="form-label text-muted small mb-1">AMC contract</Label>
          <AsyncPaginatedSelect
            fetchOptions={getAmcOptions}
            placeholder="Any contract…"
            value={filters.amcId === "" ? "" : filters.amcId}
            onChange={(opt: SelectOption | SelectOption[] | null) => {
              const val = Array.isArray(opt) ? (opt[0] ?? null) : opt;
              const v = val?.value;
              patch({
                amcId:
                  typeof v === "number" && Number.isFinite(v) && v > 0 ? v : "",
              });
            }}
          />
        </Col>
        <Col xs={12} md={4}>
          <Label className="form-label text-muted small mb-1">Address</Label>
          <Input
            type="text"
            placeholder="Visit address…"
            value={filters.address}
            onChange={(e) => patch({ address: e.target.value })}
          />
        </Col>
        <Col xs={12} sm={6} md={2}>
          <Label className="form-label text-muted small mb-1">City</Label>
          <Input
            type="text"
            value={filters.city}
            onChange={(e) => patch({ city: e.target.value })}
          />
        </Col>
        <Col xs={12} sm={6} md={2}>
          <Label className="form-label text-muted small mb-1">State</Label>
          <Input
            type="text"
            value={filters.state}
            onChange={(e) => patch({ state: e.target.value })}
          />
        </Col>
      </Row>

    </div>
  );
}
